import { useState } from "react";
import { F, D } from "../../../styles/palette";
import { DC, DCk, NI } from "../../shared";
import { ISL } from "../../../data/islStages";

export default function EvolucionForm({ user, patient, addRecord, onCancel, onSaved }) {
  const [motivo, setMotivo] = useState("");
  const [examenFisico, setExamenFisico] = useState("");
  const [stemmer, setStemmer] = useState(false);
  const [fovea, setFovea] = useState(false);
  const [cambiosCutaneos, setCambiosCutaneos] = useState("");
  const [estadioISL, setEstadioISL] = useState(patient?.estadioISL ?? null);
  const [vasDolor, setVasDolor] = useState(0);
  const [analisis, setAnalisis] = useState("");
  const [plan, setPlan] = useState("");
  const [acepto, setAcepto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Circometr\u00eda
  const [showCirco, setShowCirco] = useState(false);
  const [limb, setLimb] = useState("upper");
  const uL = ["Mu\u00f1eca", "Antebrazo", "Brazo dist.", "Brazo prox."];
  const lL = ["Tobillo", "Pantorrilla", "Rodilla", "Muslo dist.", "Muslo prox."];
  const labels = limb === "upper" ? uL : lL;
  const initArr = () => limb === "upper" ? [0, 0, 0, 0] : [0, 0, 0, 0, 0];
  const [affC, setAffC] = useState(initArr());
  const [conC, setConC] = useState(initArr());
  const [imc, setImc] = useState(25);

  const cV = cs => {
    let v = 0;
    for (let i = 0; i < cs.length - 1; i++) {
      const c1 = cs[i], c2 = cs[i + 1];
      if (c1 > 0 && c2 > 0) {
        const r1 = c1 / (2 * Math.PI), r2 = c2 / (2 * Math.PI);
        v += (Math.PI * 10 * (r1 * r1 + r1 * r2 + r2 * r2)) / 3;
      }
    }
    return v;
  };
  const aV = cV(affC), cVo = cV(conC);
  const affIdx = imc > 0 ? (aV / imc).toFixed(1) : "0";
  const conIdx = imc > 0 ? (cVo / imc).toFixed(1) : "0";
  const diffPct = cVo > 0 ? (((aV - cVo) / cVo) * 100).toFixed(1) : "0";

  const vasColor = vasDolor <= 2 ? "#16a34a" : vasDolor <= 4 ? "#ca8a04" : vasDolor <= 6 ? "#ea580c" : vasDolor <= 8 ? "#dc2626" : "#991b1b";

  const handleSave = async () => {
    if (!motivo.trim()) { setError("El motivo de consulta es requerido"); return; }
    if (!acepto) { setError("Debe certificar la informaci\u00f3n para firmar"); return; }
    setError(null);
    setSaving(true);
    try {
      const data = {
        subjetivo: { motivo },
        objetivo: {
          examenFisico,
          stemmer,
          fovea,
          cambiosCutaneos,
          estadioISL,
          vasDolor,
          circometria: showCirco ? { limb, affC, conC, imc } : null,
          volResultados: showCirco ? { affIdx, conIdx, diffPct } : null,
        },
        analisis,
        plan,
      };
      await addRecord(data, user);
      setSuccess(true);
      setTimeout(() => onSaved(), 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <DC>
        <div style={{ textAlign: "center", padding: 30 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>&#9989;</div>
          <h3 style={{ color: "#4ade80", fontSize: 18, margin: 0, fontFamily: F.b }}>Evoluci\u00f3n registrada y firmada</h3>
        </div>
      </DC>
    );
  }

  return (
    <div>
      <button onClick={onCancel} style={{ background: "none", border: "none", color: D.ac, cursor: "pointer", fontSize: 13, fontFamily: F.b, marginBottom: 12, padding: 0 }}>
        &larr; Volver a evoluciones
      </button>
      <h3 style={{ color: "#e0f2fe", fontSize: 17, margin: "0 0 14px", fontFamily: F.dm }}>Nueva Evoluci\u00f3n Cl\u00ednica</h3>

      {error && <p style={{ color: "#f87171", fontSize: 13, marginBottom: 10 }}>{error}</p>}

      {/* Subjetivo */}
      <DC title="Subjetivo" accent="#38bdf8">
        <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 5, fontFamily: F.b }}>
          Motivo de consulta / s\u00edntomas reportados <span style={{ color: "#f87171" }}>*</span>
        </label>
        <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
          style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,0.2)", background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 14, fontFamily: F.b, boxSizing: "border-box", resize: "vertical" }}
          placeholder="Describa el motivo de consulta y s\u00edntomas del paciente..." />
      </DC>

      {/* Objetivo */}
      <DC title="Objetivo" accent="#4ade80">
        <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 5, fontFamily: F.b }}>Examen f\u00edsico</label>
        <textarea value={examenFisico} onChange={e => setExamenFisico(e.target.value)} rows={3}
          style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,0.2)", background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 14, fontFamily: F.b, boxSizing: "border-box", resize: "vertical", marginBottom: 10 }}
          placeholder="Hallazgos del examen f\u00edsico..." />

        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <DCk label="Signo de Stemmer" checked={stemmer} onChange={() => setStemmer(!stemmer)} />
          <DCk label="F\u00f3vea presente" checked={fovea} onChange={() => setFovea(!fovea)} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 5, fontFamily: F.b }}>Cambios cut\u00e1neos</label>
          <input type="text" value={cambiosCutaneos} onChange={e => setCambiosCutaneos(e.target.value)}
            style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,0.2)", background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 14, fontFamily: F.b, boxSizing: "border-box" }}
            placeholder="Ej: papilomatosis, hiperqueratosis..." />
        </div>

        {/* ISL selector */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 8, fontFamily: F.b }}>Estadio ISL</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6 }}>
            {ISL.map((st, i) => {
              const active = estadioISL === i;
              return (
                <div key={i} onClick={() => setEstadioISL(i)}
                  style={{ padding: 10, borderRadius: 8, cursor: "pointer", border: `2px solid ${active ? st.c : "rgba(100,116,139,0.1)"}`, background: active ? `${st.c}15` : "rgba(30,41,59,0.4)", textAlign: "center" }}>
                  <div style={{ fontWeight: 800, fontSize: 12, color: st.c, fontFamily: F.m }}>{st.s.replace("Estadio ", "")}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{st.n}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* VAS dolor */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 5, fontFamily: F.b }}>VAS Dolor</label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="range" min={0} max={10} value={vasDolor} onChange={e => setVasDolor(Number(e.target.value))}
              style={{ flex: 1, accentColor: vasColor, height: 5 }} />
            <span style={{ color: vasColor, fontSize: 20, fontWeight: 800, fontFamily: F.m, minWidth: 30, textAlign: "right" }}>{vasDolor}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
            <span style={{ fontSize: 10, color: "#16a34a" }}>Sin dolor</span>
            <span style={{ fontSize: 10, color: "#991b1b" }}>M\u00e1ximo dolor</span>
          </div>
        </div>

        {/* Circometr\u00eda */}
        <div style={{ marginTop: 8 }}>
          <button onClick={() => { setShowCirco(!showCirco); if (!showCirco) { setAffC(initArr()); setConC(initArr()); } }}
            style={{ background: "none", border: `1px solid ${D.bdr}`, borderRadius: 8, padding: "8px 14px", color: D.ac, cursor: "pointer", fontSize: 12, fontFamily: F.b, width: "100%" }}>
            {showCirco ? "Ocultar circometr\u00eda" : "Agregar circometr\u00eda (volumen)"}
          </button>

          {showCirco && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                {["upper", "lower"].map(l => (
                  <button key={l} onClick={() => { setLimb(l); const a = l === "upper" ? [0,0,0,0] : [0,0,0,0,0]; setAffC(a); setConC([...a]); }}
                    style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${limb === l ? "#0284c7" : "rgba(100,116,139,0.15)"}`, background: limb === l ? "rgba(2,132,199,0.1)" : "transparent", color: limb === l ? "#0284c7" : "#64748b", fontSize: 12, cursor: "pointer", fontWeight: 600, fontFamily: F.b }}>
                    {l === "upper" ? "UEL (Superior)" : "LEL (Inferior)"}
                  </button>
                ))}
              </div>
              <NI label="IMC" value={imc} onChange={setImc} unit="kg/m\u00b2" min={10} max={60} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                <DC title="Afectado" accent="#dc2626">
                  {labels.map((l, i) => <NI key={`a${i}`} label={l} value={affC[i]} onChange={v => { const n = [...affC]; n[i] = v; setAffC(n); }} unit="cm" min={0} max={200} />)}
                </DC>
                <DC title="Contralateral" accent="#16a34a">
                  {labels.map((l, i) => <NI key={`c${i}`} label={l} value={conC[i]} onChange={v => { const n = [...conC]; n[i] = v; setConC(n); }} unit="cm" min={0} max={200} />)}
                </DC>
              </div>
              {/* Resultados */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
                {[
                  { l: "\u00cdndice Afectado", v: affIdx, c: "#dc2626" },
                  { l: "\u00cdndice Sano", v: conIdx, c: "#16a34a" },
                  { l: "Dif. Volum\u00e9trica", v: `${diffPct}%`, c: Math.abs(parseFloat(diffPct)) >= 20 ? "#dc2626" : Math.abs(parseFloat(diffPct)) >= 10 ? "#ca8a04" : "#16a34a" },
                ].map((r, i) => (
                  <div key={i} style={{ textAlign: "center", padding: 10, background: "rgba(30,41,59,0.5)", borderRadius: 8 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: r.c, fontFamily: F.m }}>{r.v}</div>
                    <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{r.l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DC>

      {/* An\u00e1lisis */}
      <DC title="An\u00e1lisis" accent="#a78bfa">
        <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 5, fontFamily: F.b }}>Interpretaci\u00f3n cl\u00ednica / diagn\u00f3stico</label>
        <textarea value={analisis} onChange={e => setAnalisis(e.target.value)} rows={3}
          style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,0.2)", background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 14, fontFamily: F.b, boxSizing: "border-box", resize: "vertical" }}
          placeholder="An\u00e1lisis e interpretaci\u00f3n de hallazgos..." />
      </DC>

      {/* Plan */}
      <DC title="Plan" accent="#fb923c">
        <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 5, fontFamily: F.b }}>Plan de tratamiento</label>
        <textarea value={plan} onChange={e => setPlan(e.target.value)} rows={3}
          style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,0.2)", background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 14, fontFamily: F.b, boxSizing: "border-box", resize: "vertical" }}
          placeholder="Plan terap\u00e9utico y recomendaciones..." />
      </DC>

      {/* Firma Digital */}
      <DC title="Firma Digital" accent="#dc2626">
        <div style={{ padding: 12, background: "rgba(30,41,59,0.5)", borderRadius: 8, marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 600 }}>{user?.displayName || user?.email}</div>
          {user?.registroMedico && <div style={{ fontSize: 12, color: "#94a3b8" }}>Registro M\u00e9dico: {user.registroMedico}</div>}
          <div style={{ fontSize: 11, color: "#64748b", fontFamily: F.m, marginTop: 4 }}>{new Date().toLocaleString("es-CO")}</div>
        </div>
        <DCk
          label="Certifico que la informaci\u00f3n registrada es veraz y completa"
          checked={acepto}
          onChange={() => setAcepto(!acepto)}
          desc="Al firmar, se generar\u00e1 un hash SHA-256 de integridad conforme a la Ley 2015/2020"
        />
      </DC>

      {/* Botones */}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={onCancel}
          style={{ padding: "10px 22px", borderRadius: 8, border: `1px solid ${D.bdr}`, background: "transparent", color: "#94a3b8", fontSize: 13, cursor: "pointer", fontFamily: F.b }}>
          Cancelar
        </button>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: "10px 22px", borderRadius: 8, border: "none", background: saving ? "#64748b" : "#dc2626", color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer", fontFamily: F.b }}>
          {saving ? "Firmando..." : "Firmar y Guardar"}
        </button>
      </div>
    </div>
  );
}
